import { useMutation } from '@tanstack/react-query';
import {
  contractDocumentsControllerAddVersionV1,
  contractDocumentsControllerCreateV1,
  contractDocumentsControllerDownloadV1,
  contractDocumentsControllerRemoveV1,
} from '@/api/generated/contracts/contracts';
import type {
  AddContractDocumentVersionDto,
  CreateContractDocumentDto,
} from '@/api/generated/model';
import { useContractMutationEffects } from './useContracts';

/**
 * Takes the contract id per call rather than per hook, because `/contracts/new`
 * attaches the first document to a contract it has only just created.
 */
export function useAddContractDocumentMutation() {
  const onContract = useContractMutationEffects();
  return useMutation({
    mutationFn: ({ contractId, dto }: { contractId: string; dto: CreateContractDocumentDto }) =>
      contractDocumentsControllerCreateV1(contractId, dto),
    onSuccess: onContract,
  });
}

export function useAddContractDocumentVersionMutation(contractId: string) {
  const onContract = useContractMutationEffects();
  return useMutation({
    mutationFn: ({ documentId, dto }: { documentId: string; dto: AddContractDocumentVersionDto }) =>
      contractDocumentsControllerAddVersionV1(contractId, documentId, dto),
    onSuccess: onContract,
  });
}

export function useRemoveContractDocumentMutation(contractId: string) {
  const onContract = useContractMutationEffects();
  return useMutation({
    mutationFn: (documentId: string) => contractDocumentsControllerRemoveV1(contractId, documentId),
    onSuccess: onContract,
  });
}

/** A mutation, not a query: every call is audited and mints a fresh signed URL. */
export function useDownloadContractDocumentMutation(contractId: string) {
  return useMutation({
    mutationFn: ({ documentId, versionId }: { documentId: string; versionId?: string }) =>
      contractDocumentsControllerDownloadV1(
        contractId,
        documentId,
        versionId ? { versionId } : undefined,
      ),
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener');
    },
  });
}
